// Engagement: DAU/WAU/MAU + daily try-on / aura runs.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

type Row = { user_id: string; created_at: string; kind: string };

export default function AdminEngagement() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - 30 * 86400_000).toISOString();
      const { data } = await supabase
        .from("tryons")
        .select("user_id,created_at,kind")
        .gte("created_at", since)
        .limit(20000);
      if (!active) return;
      setRows((data ?? []) as Row[]);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const series = useMemo(() => {
    const m = new Map<string, { day: string; tryons: number; aura: number; users: Set<string> }>();
    for (const r of rows) {
      const day = r.created_at.slice(0, 10);
      if (!m.has(day)) m.set(day, { day, tryons: 0, aura: 0, users: new Set() });
      const cur = m.get(day)!;
      if (r.kind === "aura") cur.aura += 1;
      else cur.tryons += 1;
      cur.users.add(r.user_id);
    }
    return Array.from(m.values())
      .sort((a, b) => a.day.localeCompare(b.day))
      .map((p) => ({ day: p.day, tryons: p.tryons, aura: p.aura, dau: p.users.size }));
  }, [rows]);

  const totals = useMemo(() => {
    const day = new Set<string>();
    const week = new Set<string>();
    const month = new Set<string>();
    const now = Date.now();
    for (const r of rows) {
      const age = now - new Date(r.created_at).getTime();
      if (age <= 86400_000) day.add(r.user_id);
      if (age <= 7 * 86400_000) week.add(r.user_id);
      if (age <= 30 * 86400_000) month.add(r.user_id);
    }
    return { dau: day.size, wau: week.size, mau: month.size };
  }, [rows]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Engagement</h1>
        <p className="text-sm text-muted-foreground">Last 30 days.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Kpi label="DAU" value={totals.dau.toLocaleString()} />
        <Kpi label="WAU" value={totals.wau.toLocaleString()} />
        <Kpi label="MAU" value={totals.mau.toLocaleString()} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="tryons" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="aura" stroke="hsl(var(--chart-2, 199 89% 48%))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="dau" stroke="hsl(var(--chart-3, 142 71% 45%))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}
